/**
 * Защита маршрутов. Без сессии — редирект на `/login` с запоминанием адреса,
 * при несовпадении роли — экран «Недостаточно прав» (редиректом молча уводить нельзя:
 * пользователь должен понять, почему он не видит раздел).
 */

import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from './AuthContext';
import { ErrorState, LoadingState } from '../components/ErrorState';
import type { Role } from '../api/types';

export interface RequireRoleProps {
    /** Одна роль или список. Без указания — достаточно быть авторизованным. */
    role?: Role | Role[];
    children?: ReactNode;
}

export function RequireRole({ role, children }: RequireRoleProps) {
    const { status, hasRole } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    if (status === 'loading') return <LoadingState label="Проверяем доступ…" />;

    if (status === 'anonymous') {
        return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
    }

    const roles = role === undefined ? [] : Array.isArray(role) ? role : [role];
    if (roles.length > 0 && !hasRole(...roles)) {
        return (
            <ErrorState
                title="Недостаточно прав"
                message="Этот раздел доступен другой роли. Вернитесь на главный экран."
                onRetry={() => navigate('/', { replace: true })}
                retryLabel="На главную"
            />
        );
    }

    return <>{children ?? <Outlet />}</>;
}

/** То же самое без проверки роли — читается понятнее в разметке маршрутов. */
export function RequireAuth({ children }: { children?: ReactNode }) {
    return <RequireRole>{children}</RequireRole>;
}

export default RequireRole;
